import { parseMultipleJSON, type IStreamChatItem } from "../../vendor/agentUiStreaming";

export type IPresentationChatItem = IStreamChatItem;
export interface IPresentationChatMessage {
  id: string;
  sender: "user" | "assistant";
  chatItems: IPresentationChatItem[];
  plan?: unknown[];
  agentId?: string;
  isStreaming?: boolean;
  metadata?: Record<string, unknown>;
  attachments?: Array<{ id: string; name?: string; mimeType?: string; size?: number; previewUrl?: string }>;
}

export interface IPresentationSessionResource {
  _id?: string;
  resource_id?: string;
  default_chat_input?: string;
  session?: { locked?: boolean; [key: string]: unknown };
  chat_history?: { messages?: Array<{
    type: string;
    data: { content: string; additional_kwargs?: { file_ids?: string[]; timestamp?: string } };
  }> };
  [key: string]: unknown;
}

// Saved transcripts contain separate progress events; the chat UI expects one
// progress item with intermediate steps, matching the SDK's streamed shape.
const groupProgress = (items: IPresentationChatItem[]): IPresentationChatItem[] => {
  const result: IPresentationChatItem[] = [];
  const progress = new Map<string, IPresentationChatItem>();
  for (const item of items) {
    if (item.type !== "progress" || !item.exec_id) {
      result.push(item);
      continue;
    }
    const parent = progress.get(item.exec_id);
    if (item.step === "start") {
      const next = { ...item, id: `progress-${item.exec_id}`, intermediateSteps: [] };
      progress.set(item.exec_id, next);
      result.push(next);
    } else if (parent) {
      const steps = parent.intermediateSteps ?? [];
      if (steps.length) steps[steps.length - 1].step = "done";
      if (item.step === "done") parent.step = "done";
      else steps.push({ step: item.step || "", message: item.message || "", label: item.label });
      parent.intermediateSteps = steps;
    }
  }
  return result;
};

export const toPresentationChatMessages = (resource: IPresentationSessionResource): IPresentationChatMessage[] =>
  (resource.chat_history?.messages ?? []).map((message, index) => {
    const sender = message.type === "human" ? "user" : "assistant";
    const id = `${sender}-${resource._id || resource.resource_id}-${index}`;
    const textItem = { type: "text", id: `${id}-text`, message: message.data.content };
    if (sender === "user") {
      return {
        id, sender, chatItems: [textItem],
        attachments: message.data.additional_kwargs?.file_ids?.map((fileId) => ({ id: fileId })),
      };
    }
    let parsed: ReturnType<typeof parseMultipleJSON>;
    try { parsed = parseMultipleJSON(message.data.content); }
    catch { parsed = {}; }
    const timestamp = message.data.additional_kwargs?.timestamp;
    return {
      id, sender,
      chatItems: groupProgress((parsed.data ?? (parsed.plan ? [] : [textItem])).map((item) => timestamp ? { ...item, timestamp } : item)),
      plan: parsed.plan || [],
    };
  });
